import React from 'react';
import {useNavigation} from '@react-navigation/native';
import {
  calculateWithdrawReceived,
  resolveWithdrawWebLabels,
  resolveMaxWithdrawAmount,
  useWithdrawSessionStore,
  useWithdrawStore as useProgressiveWithdrawStore,
  validateWithdrawSubmit,
  WithdrawWebHomePage,
  type WithdrawPayType,
} from '@fe-progressive/package-progressive/withdraw';
import {ProgressiveWebNavBar} from '@fe-progressive/package-progressive/progressive-web-nav';
import {showProgressiveHostToast} from '@fe-progressive/package-progressive/progressive-toast';
import {goBack, goCS, goTo, toPriceStr} from '@/utils';
import globalStore from '@/services/global.state';
import theme from '@/style';
import {IUserInfo, postUserInfo} from '@/services/global.service';
import {
  CryptoCoin,
  getCryptoCoinList,
  getPayConfig,
  PayConfig,
  postCryptoWithdrawal,
} from '@/services/pay.service';
import {getPaymentTypeAvailability} from '@/services/payment-type-config';
import {BasicObject} from '@/types';
import {useTranslation} from 'react-i18next';
import useWithdrawAmountOptions from '../withdraw-new/hooks/useWithdrawAmountOptions';
import useHostWithdrawStore from '../withdraw-new/withdraw.store';
import {
  CardListItemType,
  getBankList,
  getWithdrawInfo,
  onWithdraw,
} from './withdraw-service';
import {
  useWithdrawThemeVariables,
  useWithdrawProgressiveViewportHeightStyle,
  withdrawThemeStyle,
} from './withdraw-progressive-theme';
import './withdraw-progressive.scss';

const WITHDRAW_SUBMIT_MESSAGE_KEY = {
  needLogin: '',
  noWithdrawable: 'withdraw-page.error.noWithdrawable',
  addAmount: 'withdraw-page.error.addAmount',
  addCard: 'withdraw-page.error.addCard',
  selectCoin: 'withdraw-page.error.selectCoin',
  addAddress: 'withdraw-page.error.addAddress',
  invalidAddress: 'withdraw-page.error.invalidAddress',
  minAmount: 'withdraw-page.error.minAmount',
  maxAmount: 'withdraw-page.error.maxAmount',
} as const;

const Withdraw = () => {
  const {i18n} = useTranslation();
  const navigation = useNavigation();
  useWithdrawThemeVariables();
  const [loading, setLoading] = React.useState(false);
  const [cardList, setCardList] = React.useState<CardListItemType[]>([]);
  const [showCard, setShowCard] = React.useState(false);
  const [price, setPrice] = React.useState('');
  const [selectedCard, setSelectedCard] = React.useState<string | undefined>();
  const [user, setUser] = React.useState<IUserInfo | BasicObject>({});
  const [payType, setPayType] = React.useState<WithdrawPayType | null>(null);
  const [configStatus, setConfigStatus] = React.useState<
    'loading' | 'ready' | 'error'
  >('loading');
  const [configAttempt, setConfigAttempt] = React.useState(0);
  const [cryptoCoinList, setCryptoCoinList] = React.useState<CryptoCoin[]>([]);
  const [selectedCoin, setSelectedCoin] = React.useState<CryptoCoin | null>(
    null,
  );
  const [payConfig, setPayConfig] = React.useState<PayConfig | null>(null);
  const [recipientAddress, setRecipientAddress] = React.useState('');
  const [showCoinSelector, setShowCoinSelector] = React.useState(false);
  const setHostWithdrawInfo = useHostWithdrawStore(s => s.setWithdrawInfo);
  const withdrawInfo = useHostWithdrawStore(s => s.info);
  const selectedAmount = useHostWithdrawStore(s => s.selectedAmount);
  const progressiveWithdraw = useProgressiveWithdrawStore;
  const progressiveSession = useWithdrawSessionStore;
  const requestIdRef = React.useRef(0);
  const {fiatEnabled, cryptoEnabled} = getPaymentTypeAvailability(payConfig);
  const canLoadWithdrawData = configStatus === 'ready' && payType !== null;
  const viewportHeightStyle = useWithdrawProgressiveViewportHeightStyle();
  const {options: rawAmountOptions, loading: amountOptionsLoading} =
    useWithdrawAmountOptions(payType === 'CRYPTO', canLoadWithdrawData);

  React.useEffect(() => {
    progressiveSession.getState().syncSession({
      isLoggedIn: Boolean(globalStore.token),
      tokenReady: true,
      userInfo: Object.keys(user).length > 0 ? user : null,
    });
  }, [progressiveSession, user]);

  React.useEffect(() => {
    if (selectedAmount === 0) {
      setPrice(
        resolveMaxWithdrawAmount(
          withdrawInfo?.withdrawableBalance || 0,
          payConfig?.cryptoWithdrawalAmountLimit,
        ).toString(),
      );
    } else if (selectedAmount) {
      setPrice(selectedAmount.toString());
    }
  }, [
    payConfig?.cryptoWithdrawalAmountLimit,
    selectedAmount,
    withdrawInfo?.withdrawableBalance,
  ]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    getPayConfig()
      .then(config => {
        if (!active) {
          return;
        }
        const availability = getPaymentTypeAvailability(config);
        const nextType: WithdrawPayType | null = availability.fiatEnabled
          ? 'UPI'
          : availability.cryptoEnabled
          ? 'CRYPTO'
          : null;
        setPayConfig(config);
        setPayType(nextType);
        setConfigStatus('ready');
        progressiveWithdraw.getState().setPayConfig(config);
        if (nextType) {
          progressiveWithdraw.getState().setPayType(nextType);
        }
      })
      .catch(() => {
        if (active) {
          setPayType(null);
          setConfigStatus('error');
          setLoading(false);
        }
      });
    return () => {
      active = false;
      requestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configAttempt]);

  React.useEffect(() => {
    if (!canLoadWithdrawData) {
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const requests = [getUserInfo(), getWithDrawInfo()];
    if (fiatEnabled) {
      requests.push(onGetBankList());
    }
    if (cryptoEnabled) {
      requests.push(fetchCryptoData());
    }
    Promise.allSettled(requests).finally(() => {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    });
    const unsubscribe = fiatEnabled
      ? navigation.addListener('focus', () => {
          onGetBankList();
        })
      : undefined;
    return () => {
      requestIdRef.current += 1;
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, canLoadWithdrawData, fiatEnabled, cryptoEnabled]);

  const fetchCryptoData = async () => {
    try {
      const requestId = requestIdRef.current;
      const coins = await getCryptoCoinList();
      if (requestId !== requestIdRef.current) {
        return;
      }
      setCryptoCoinList(coins);
      setSelectedCoin(current =>
        current && coins.some(coin => coin.code === current.code)
          ? current
          : coins[0] || null,
      );
    } catch (error) {
      console.error('Failed to fetch crypto data:', error);
    }
  };

  const getUserInfo = async () => {
    const requestId = requestIdRef.current;
    const res = await postUserInfo();
    if (requestId !== requestIdRef.current) {
      return;
    }
    setUser(res);
    progressiveSession.getState().syncSession({
      isLoggedIn: Boolean(globalStore.token),
      tokenReady: true,
      userInfo: res,
    });
  };

  const onGetBankList = async () => {
    const requestId = requestIdRef.current;
    const res = (await getBankList()) || [];
    if (requestId !== requestIdRef.current) {
      return;
    }
    setSelectedCard(current =>
      current && res.some(item => item.id === current) ? current : res[0]?.id,
    );
    setCardList(res);
    progressiveWithdraw.getState().setAccounts(res);
  };

  const getWithDrawInfo = async () => {
    const requestId = requestIdRef.current;
    const res = await getWithdrawInfo();
    if (requestId !== requestIdRef.current) {
      return;
    }
    setHostWithdrawInfo(res);
    progressiveWithdraw.getState().setWithdrawInfo(res);
  };

  const onGoAddBank = (
    cardInfo?: CardListItemType,
    ifscCodeError?: boolean,
  ) => {
    if (!globalStore.token) {
      goTo('Login', {backPage: 'Home'});
      return;
    }
    goTo('AddBankProgressive', {
      ifscCodeError,
      isFirst: cardList.length === 0 ? '1' : '0',
      cardInfo,
    });
  };

  const onWithdrawSubmit = async () => {
    try {
      if (
        !payType ||
        (payType === 'UPI' && !fiatEnabled) ||
        (payType === 'CRYPTO' && !cryptoEnabled)
      ) {
        return;
      }
      if (payType === 'CRYPTO' && selectedCoin?.addressRegex) {
        try {
          RegExp(selectedCoin.addressRegex);
        } catch {
          showProgressiveHostToast({
            type: 'warning',
            message: i18n.t('withdraw-page.error.invalidAddress'),
          });
          return;
        }
      }
      const validation = validateWithdrawSubmit({
        isLoggedIn: progressiveSession.getState().ensureSession().ok,
        payType,
        amount: price,
        withdrawableBalance: withdrawInfo?.withdrawableBalance,
        selectedAccountId: selectedCard,
        selectedCoin,
        recipientAddress,
        payConfig,
      });

      if (!validation.ok) {
        if (validation.issue === 'needLogin') {
          goTo('Login', {backPage: 'Home'});
          return;
        }
        const key = validation.issue
          ? WITHDRAW_SUBMIT_MESSAGE_KEY[validation.issue]
          : '';
        if (key) {
          showProgressiveHostToast({
            type: 'warning',
            message: i18n.t(key, validation.params),
          });
        }
        return;
      }

      setLoading(true);
      if (payType === 'UPI') {
        await onWithdraw({
          cardId: selectedCard as string,
          price: Number(price),
        });
      } else {
        await postCryptoWithdrawal({
          payAddress: recipientAddress,
          amount: Number(price),
          payCurrency: (selectedCoin as CryptoCoin).code,
        });
      }

      globalStore.updateAmount.next({});
      const received = calculateWithdrawReceived(
        price,
        withdrawInfo?.withdrawFeeRate || 0,
      );
      goTo('WithdrawSuccessProgressive', {amount: received});
    } catch (e: any) {
      if (payType === 'UPI' && e.data?.code === 9601) {
        const card = cardList.find(item => item.id === selectedCard);
        onGoAddBank(card, true);
      }
    } finally {
      setLoading(false);
    }
  };

  const amountOptions = React.useMemo(
    () =>
      rawAmountOptions.filter(
        (option): option is number | 'Max' =>
          typeof option === 'number' || option === 'Max',
      ),
    [rawAmountOptions],
  );

  const handleGotoRecords = () => {
    if (!globalStore.token) {
      goTo('Login', {backPage: 'Home'});
      return;
    }
    goTo('WithdrawRecords');
  };

  const handlePayTypeChange = (nextType: WithdrawPayType) => {
    if (
      (nextType === 'UPI' && !fiatEnabled) ||
      (nextType === 'CRYPTO' && !cryptoEnabled)
    ) {
      return;
    }
    setShowCard(false);
    setShowCoinSelector(false);
    setPayType(nextType);
    progressiveWithdraw.getState().setPayType(nextType);
  };

  if (configStatus !== 'ready' || !payType) {
    return (
      <div
        className="withdraw-progressive-page overflow-hidden bg-[#f4f6fb]"
        style={{...withdrawThemeStyle, ...viewportHeightStyle}}>
        <ProgressiveWebNavBar
          title={resolveWithdrawWebLabels(i18n.language).withdraw}
          onBack={goBack}
          onCustomerService={goCS}
        />
        <div className="flex h-full flex-col items-center justify-center text-gray-500">
          {configStatus === 'loading'
            ? i18n.t('loading')
            : configStatus === 'error'
            ? i18n.t('paymentConfig.loadFailed')
            : i18n.t('withdraw-page.unavailable')}
          {configStatus === 'error' ? (
            <button
              type="button"
              className="mt-3 rounded bg-c1 px-4 py-2 text-white"
              onClick={() => {
                setConfigStatus('loading');
                setConfigAttempt(attempt => attempt + 1);
              }}>
              {i18n.t('paymentConfig.retry')}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="withdraw-progressive-page overflow-hidden bg-[#f4f6fb]"
      style={{...withdrawThemeStyle, ...viewportHeightStyle}}>
      <WithdrawWebHomePage
        payType={payType}
        amount={price}
        withdrawInfo={withdrawInfo}
        accounts={cardList}
        selectedAccountId={selectedCard}
        amountOptions={amountOptions}
        cryptoCoins={cryptoCoinList}
        selectedCoinCode={selectedCoin?.code}
        recipientAddress={recipientAddress}
        payConfig={payConfig}
        showPayTypeTabs={fiatEnabled && cryptoEnabled}
        loading={loading || amountOptionsLoading}
        currencySymbol={globalStore.currency}
        addBankIconColor={theme.basicColor.primary}
        formatAmount={value =>
          toPriceStr(Number(value || 0), {
            thousands: true,
            spacing: true,
            fixed: 2,
            currency: globalStore.currency,
          })
        }
        onBack={goBack}
        onCustomerService={goCS}
        onRecords={handleGotoRecords}
        onPayTypeChange={handlePayTypeChange}
        onAmountChange={setPrice}
        onAccountSelect={() => setShowCard(true)}
        onAddBank={() => onGoAddBank()}
        accountPopupVisible={showCard}
        onAccountPopupClose={() => setShowCard(false)}
        onAccountChange={cardId => setSelectedCard(cardId)}
        onAccountEdit={card => {
          setShowCard(false);
          onGoAddBank(card as CardListItemType);
        }}
        accountPopupCloseOnSelect={false}
        onCryptoCoinSelect={() => setShowCoinSelector(true)}
        cryptoCoinPopupVisible={showCoinSelector}
        onCryptoCoinPopupClose={() => setShowCoinSelector(false)}
        onCryptoCoinChange={coin => {
          setSelectedCoin(coin as CryptoCoin);
          setShowCoinSelector(false);
        }}
        onRecipientAddressChange={setRecipientAddress}
        onSubmit={onWithdrawSubmit}
      />
    </div>
  );
};

export default Withdraw;
